import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { selectAggregationForSelectedReport, selectExposure, selectWorkingDefinition } from 'app/area/favourite/selectors';
import { getCaseTypeIds } from 'app/area/reports/caseTypeIds';
import { selectSelectionFieldItemsForCurrentReport } from 'app/area/reports/containers/group-by/selectors';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators } from './actions';

export function useActionCreators(api: ReportCommonApi) {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();
  return useMemo(() => bindActionCreators(actionCreators(api, instanceKey), dispatch), [dispatch, api, instanceKey]);
}

export function useWorkingDefinitionSelectedGroupByFields() {
  const instanceKey = useInstanceKey();
  return useSelector(selectSelectionFieldItemsForCurrentReport(instanceKey));
}

export function useLoadInitialDataEffect(api: ReportCommonApi) {
  const {
    loadGroupBySortOrderType,
    clearState
  } = useActionCreators(api);

  useEffect(() => {
    loadGroupBySortOrderType();
    return () => {
      clearState();
    };
  }, [loadGroupBySortOrderType, clearState]);
}

export function useLoadGroupByDefaultFieldsFrequencyReportEffect(api: ReportCommonApi) {
  const instanceKey = useInstanceKey();

  const {
    loadGroupByDefaultFields,
    updateDefaultGroupByFields
  } = useActionCreators(api);

  const workingDefinition = useSelector(selectWorkingDefinition(instanceKey));

  const {
    aggregationDomain,
    aggregationType
  } = useSelector(selectAggregationForSelectedReport(instanceKey));

  const {
    exposureApplication
  } = useSelector(selectExposure(instanceKey));

  useEffect(() => {
    if (exposureApplication?.v && aggregationDomain?.v && aggregationType?.v) {
      loadGroupByDefaultFields();
      return () => {
        updateDefaultGroupByFields([]);
      };
    }
    return undefined;
  }, [
    exposureApplication?.v,
    aggregationDomain?.v,
    aggregationType?.v,
    JSON.stringify(getCaseTypeIds(workingDefinition))
  ]);
}

export function useLoadGroupByDefaultFieldsSelectionReportEffect(api: ReportCommonApi) {
  const {
    loadGroupByDefaultFields,
    updateDefaultGroupByFields
  } = useActionCreators(api);
  
  useEffect(() => {
    loadGroupByDefaultFields();
    return () => {
      updateDefaultGroupByFields([]);
    };
  }, []);
}

export function useLoadSelectionTypesEffect(api: ReportCommonApi) {
  const selectedGroupByFields = useWorkingDefinitionSelectedGroupByFields();

  const {
    loadGroupBySelectionTypes
  } = useActionCreators(api);

  const fieldIds = selectedGroupByFields.map(s => s?.field?.value);

  useEffect(() => {
    const loadAll = async () => await Promise.all([fieldIds.map(loadGroupBySelectionTypes)]);
    loadAll();
  }, [JSON.stringify(fieldIds)]);
}
